import type {
  Correlations,
  CorrelationsRequest,
  CorrelationsUpdateCorrelation
} from '@library/types/correlations';
import { count, equals, includes, omit } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { findElementIndexInObjectList } from '@bo-utils/common';
import { transformCorrelations } from '@bo-utils/correlations';
import { useGetCorrelationsQuery } from '@bo-services/correlationsApi';
import { useImmer } from 'use-immer';

export const useCorrelations = ({
  queryParams
}: {
  queryParams: CorrelationsRequest;
}) => {
  const [correlations, setCorrelations] = useImmer<Correlations>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    isSameTeam,
    statType1,
    statType2,
    firstPosition,
    secondPosition,
    firstChoice,
    secondChoice
  } = queryParams;

  const {
    data: correlationsData,
    isError,
    isFetching,
    isLoading,
    isSuccess,
    refetch
  } = useGetCorrelationsQuery({
    isSameTeam,
    page: currentPage,
    pageSize: 50,
    statType1,
    statType2,
    firstPosition,
    secondPosition,
    firstChoice,
    secondChoice
  });

  const isRunning = isFetching || isLoading;

  const correlationsTransformed = useMemo(
    () =>
      !isRunning && isSuccess
        ? transformCorrelations({
            correlationsData
          })
        : [],
    [correlationsData, isRunning, isSuccess]
  );

  useEffect(() => {
    if (!isRunning && isSuccess) {
      setCorrelations(correlationsTransformed);
      return;

      const newCorrelation = correlationsTransformed[0];

      if (!includes(newCorrelation, correlations)) {
        setCorrelations((draft) => {
          draft.push(...correlationsTransformed);
        });
      }
    }
  }, [
    correlations,
    correlationsTransformed,
    isRunning,
    isSuccess,
    setCorrelations
  ]);

  const handleCorrelationsChange = useCallback(
    ({ initialCorrelation, key, value }: CorrelationsUpdateCorrelation) => {
      const { correlationId } = initialCorrelation;
      setCorrelations((draft) => {
        const draftCorrelationIndex = findElementIndexInObjectList(
          draft,
          'correlationId',
          correlationId
        );

        if (draftCorrelationIndex === -1) {
          return;
        }

        draft[draftCorrelationIndex][key] = value;

        if (key === 'rowStatus' && value === 'DELETED') {
          return;
        }

        draft[draftCorrelationIndex].rowStatus = !equals(
          omit(['rowStatus'], draft[draftCorrelationIndex]),
          omit(['rowStatus'], initialCorrelation)
        )
          ? 'UPDATED'
          : undefined;

        const { approvedBy, isTouched, requestedBy } =
          draft[draftCorrelationIndex];

        draft[draftCorrelationIndex].approvedBy = isTouched ? approvedBy : '';
        draft[draftCorrelationIndex].requestedBy = isTouched ? requestedBy : '';

        draft[draftCorrelationIndex].isTouched =
          draft[draftCorrelationIndex].rowStatus === 'UPDATED' && !isTouched
            ? true
            : isTouched;
      });
    },
    [setCorrelations]
  );

  const handleCorrelationsRefetch = async () => {
    await refetch();
  };

  const handleResetCorrelations = () => {
    setCurrentPage(1);
    setCorrelations([]);
  };

  const totalChanges = useMemo(
    () => count((correlation) => !!correlation.rowStatus, correlations),
    [correlations]
  );

  const hasModifiedCorrelations = useMemo(
    () => correlations.some((correlation) => correlation.isTouched),
    [correlations]
  );

  const handlePageChange = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  return {
    correlations,
    currentPage,
    handleCorrelationsChange,
    handleCorrelationsRefetch,
    hasModifiedCorrelations,
    handlePageChange,
    handleResetCorrelations,
    isRunning,
    isSuccess,
    isError,
    totalChanges,
    totalPages: correlationsData?.meta.total_pages
  };
};
